import { isEmpty, isNotDefined } from '@sgme/fp';
import { Dropdown } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback, useContext, useRef } from 'react';
import { useIntl } from 'react-intl';
import { v4 } from 'uuid';

import { getMaxDate } from '@/api/dates/utils.ts';
import { selectGetCurrentUser } from '@/api/user/index.ts';
import { BACKGROUND_LIGHT_COLOR } from '@/App/tiles/CashTile/constants.ts';
import { PredealChecksIndicator } from '@/App/tiles/shared/PredealChecksIndicator.tsx';
import {
  TargetAccumulatorContext,
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import ToggleProduct from '@/App/tiles/ToggleProduct.tsx';
import { ChangeClientDropDownItem } from '@/components/ClientSelector/ChangeClientDropDownItem.tsx';
import { TileOverridenClientIndicator } from '@/components/ClientSelector/TileOverridenClientIndicator.tsx';
import { isDefined } from '@/components/sg-design-system/utils/index.ts';
import CurrencyPairPicker from '@/components/ui/CurrencyPairPicker/index.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { targetAccumulatorSlice } from '@/store/targetAccumulator/slice.ts';
import { copyToClipBoardTargetAccumulatorThunk, resetTargetAccumulatorThunk } from '@/store/targetAccumulator/thunk.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { duplicateTile, removeTile } from '@/store/tiles/slice.ts';

export const TargetAccumulatorHeader = () => {
  const { tileId } = useContext(TargetAccumulatorContext);
  const { formatNumber } = useIntl();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const dispatch = useAppDispatch();

  const dropdownToggleRef = useRef<HTMLButtonElement>(null);
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));
  const currentUserCache = useAppSelector((state) => selectGetCurrentUser(state).data?.clients);

  const { currencyPair, status, globalPreDealCheck, predealChecks, tileClientId } = useTargetAccumulatorSelector(
    (targetAccumulator) => {
      if (isNotDefined(targetAccumulator)) {
        return {};
      }

      const predealChecks =
        targetAccumulator.properties.rfs?.status === 'PRICING'
          ? targetAccumulator.properties.rfs.quote.predealChecks
          : undefined;
      const globalPreDealCheck = isNotDefined(predealChecks)
        ? 'Unknown'
        : predealChecks.filter((check) => check.type === 'Icon' && check.statusHealth === 'Nok').length > 0
          ? 'Nok'
          : 'Ok';

      return {
        currencyPair: targetAccumulator.properties.currencyPair,
        globalPreDealCheck,
        predealChecks,
        status: targetAccumulator?.tradeCapture?.status,
        tileClientId: targetAccumulator?.clientId,
      };
    },
  );

  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();

  const handleCurrencyPairChange = useCallback(
    async (value: string) => {
      if (isDefined(status) && isDefined(currentUserCache) && isDefined(clientId)) {
        const maxDate = await getMaxDate(clientId, currentUserCache, dispatch, value);
        dispatch(targetAccumulatorSlice.actions.setTargetAccumulatorMaxDate({ tileId, maxDate }));
      }
      sgwtWebAnalytics?.trackEvent('TargetAccumulator', 'Currency pair changed', value);

      if (status !== 'INITIALIZED') {
        dispatch(resetTargetAccumulatorThunk({ tileId, currencyPair: value }));
      } else {
        checkTargetAccumulatorWithTradeCapture({
          currencyPair: value,
        });
      }
    },
    [checkTargetAccumulatorWithTradeCapture, sgwtWebAnalytics, currentUserCache, status, clientId, tileId],
  );

  const handleDuplicate = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('TargetAccumulator', 'Tile duplicated', tileId);
    dispatch(duplicateTile({ sourceTileId: tileId, newTileId: v4() }));
  }, [sgwtWebAnalytics, tileId]);

  const handleReset = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('TargetAccumulator', 'Tile reseted', tileId);
    dispatch(resetTargetAccumulatorThunk({ tileId }));
  }, [sgwtWebAnalytics, tileId]);

  const handleClose = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('TargetAccumulator', 'Tile closed', tileId);
    dispatch(removeTile(tileId));
  }, [sgwtWebAnalytics, tileId]);

  const handleCopyToClipboard = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('TargetAccumulator', 'Tile copied to clipboard', tileId);
    dispatch(copyToClipBoardTargetAccumulatorThunk({ tileId, formatNumber }));
  }, [sgwtWebAnalytics, tileId, formatNumber]);

  const handleSelectClient = useCallback(
    (clientId: number) => {
      sgwtWebAnalytics?.trackEvent('TargetAccumulator', 'Tile client selected', String(clientId));
      dispatch(
        targetAccumulatorSlice.actions.targetAccumulatorClientIdUpdated({
          tileId,
          clientId,
          isTabClientOverriden: true,
        }),
      );
    },
    [sgwtWebAnalytics, tileId],
  );

  const handleClearSelectedClient = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('TargetAccumulator', 'Tile client reseted', tileId);
    dispatch(
      targetAccumulatorSlice.actions.targetAccumulatorClientIdUpdated({
        tileId,
        clientId: undefined,
        isTabClientOverriden: true,
      }),
    );
  }, [sgwtWebAnalytics, tileId]);

  return (
    <>
      <TileOverridenClientIndicator clientId={tileClientId} onRemoveOverride={handleClearSelectedClient} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }} className="pb-8px">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ToggleProduct tileId={tileId} />
          <PredealChecksIndicator predealChecks={predealChecks} globalPreDealCheck={globalPreDealCheck ?? 'Unknown'} />
        </div>

        <CurrencyPairPicker
          tradeType="Accumulator"
          value={currencyPair}
          onChange={handleCurrencyPairChange}
          data-e2e="currency-pair-input"
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Dropdown color={BACKGROUND_LIGHT_COLOR} direction="auto" size="md" data-e2e="tile-menu">
            <Dropdown.Toggle className="btn-icon" data-e2e="tileMenu-btn">
              <i className="icon" ref={dropdownToggleRef}>
                more_vert
              </i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <ChangeClientDropDownItem triggerRef={dropdownToggleRef} onSelectClient={handleSelectClient} />
              <Dropdown.Item
                onClick={handleDuplicate}
                state={isEmpty(currencyPair) ? 'disabled' : undefined}
                data-e2e="duplicate-tile-menu"
              >
                Duplicate
              </Dropdown.Item>
              <Dropdown.Item
                onClick={handleReset}
                state={isEmpty(currencyPair) ? 'disabled' : undefined}
                data-e2e="reset-tile-menu"
              >
                Reset
              </Dropdown.Item>
              <Dropdown.Item
                onClick={handleCopyToClipboard}
                state={isEmpty(currencyPair) ? 'disabled' : undefined}
                data-e2e="copyToClipboard-tile-menu"
              >
                Copy to clipboard
              </Dropdown.Item>
              <Dropdown.Item onClick={handleClose} data-e2e="close-tile-menu">
                Close
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </>
  );
};
